import { useEffect, useState } from "react";
import { Share, X } from "lucide-react";

const STORAGE_KEY = "trace_ios_install_dismissed_at";
// Re-show every 30 days if the user dismissed without installing.
const REPROMPT_AFTER_MS = 30 * 24 * 60 * 60 * 1000;

function isIosSafari(): boolean {
  const ua = window.navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/.test(ua);
  if (!isIos) return false;
  // Exclude in-app browsers (Chrome iOS, Firefox iOS, FB, IG, ...)
  // Real Safari has "Safari/" but NOT "CriOS/", "FxiOS/", "EdgiOS/", "FBAN/", "Instagram"
  if (/CriOS|FxiOS|EdgiOS|FBAN|FBAV|Instagram|Line\//.test(ua)) return false;
  return /Safari/.test(ua);
}

function isStandalone(): boolean {
  // iOS-specific
  if ((window.navigator as unknown as { standalone?: boolean }).standalone) {
    return true;
  }
  // Cross-browser fallback
  return window.matchMedia("(display-mode: standalone)").matches;
}

function isDismissedRecently(): boolean {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return false;
  const ts = Number(stored);
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts < REPROMPT_AFTER_MS;
}

/**
 * Banner shown only on iOS Safari (real Safari, not in-app browsers) when
 * the app is NOT already installed to home screen. Tells the user how to
 * install — iOS doesn't expose a `beforeinstallprompt` event so we can't
 * trigger the system install dialog programmatically.
 *
 * Dismissable, with a 30-day cool-off before re-appearing.
 */
export function IOSInstallBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (
      isIosSafari() &&
      !isStandalone() &&
      !isDismissedRecently()
    ) {
      setVisible(true);
    }
  }, []);

  function dismiss() {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      className="ios-install-banner fixed bottom-4 left-4 right-4 z-40 rounded-ds border border-accent/40 bg-panel/95 p-4 shadow-2xl backdrop-blur-md sm:left-auto sm:right-4 sm:max-w-sm"
      role="dialog"
      aria-label="Install TRACE on your iPhone"
    >
      <button
        onClick={dismiss}
        className="absolute right-2 top-2 rounded-md p-1 text-ink-3 transition-colors hover:bg-panel-2 hover:text-ink-2"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="pr-6">
        <p className="text-sm font-medium text-ink">
          Install TRACE on your iPhone
        </p>
        <p className="mt-1.5 text-xs text-ink-3">
          Apri il menu condivisione di Safari{" "}
          <Share className="inline h-3.5 w-3.5 align-text-bottom" />, scorri e
          scegli{" "}
          <span className="font-medium text-ink">
            Aggiungi a Home
          </span>
          . TRACE si aprirà dalla schermata Home, a tutto schermo.
        </p>
      </div>
    </div>
  );
}
